import {
  AgencyData,
  AgencyAnalysisData,
  ViewType,
  Insight,
} from './AgencyAnalysisChart.types'
import {
  formatPrice as formatCentralizedPrice,
  formatNumber as formatCentralizedNumber,
  formatPercentage as formatCentralizedPercentage,
} from '../../../utils/number/formatUtils'

export const formatNumber = (num: number): string => {
  return formatCentralizedNumber(num)
}

export const formatPrice = (
  price: number,
  isRental: boolean = false
): string => {
  return formatCentralizedPrice(price, isRental)
}

export const formatPercentage = (value: number): string => {
  return formatCentralizedPercentage(value)
}

export const processAgencyData = (
  aggregations: any,
  minListings: number
): AgencyAnalysisData => {
  // Check for the correct nested structure
  const entitiesAgg = aggregations?.entities
  if (!entitiesAgg?.entity_names?.buckets) {
    return {
      agencies: [],
      totalListings: 0,
      totalAgencies: 0,
      marketLeader: null,
      top5Share: 0,
    }
  }

  const totalListings = aggregations.total_listings?.value || 0
  const allAgencies = entitiesAgg.entity_names.buckets

  // Filter and transform agencies
  const processedAgencies = allAgencies
    .filter((agency: any) => agency.back_to_listing.doc_count >= minListings)
    .map((agency: any): AgencyData => {
      const listingCount = agency.back_to_listing.doc_count
      const avgPrice = agency.back_to_listing.avg_price?.value || 0
      const totalValue = agency.back_to_listing.total_value?.value || 0
      const regions =
        agency.back_to_listing.regions?.buckets.map((r: any) => r.key) || []
      const propertyTypes =
        agency.back_to_listing.property_types?.buckets.map((pt: any) => ({
          type: pt.key,
          count: pt.doc_count,
        })) || []

      return {
        name: agency.key || 'Unknown',
        listingCount,
        avgPrice,
        totalValue,
        regionCount: regions.length,
        marketShare: (listingCount / totalListings) * 100,
        regions,
        propertyTypes,
      }
    })
    .sort((a: AgencyData, b: AgencyData) => b.listingCount - a.listingCount)

  // Calculate top 5 market share
  const top5 = processedAgencies.slice(0, 5)
  const top5Total = top5.reduce(
    (sum: number, a: AgencyData) => sum + a.listingCount,
    0
  )
  const top5Share = totalListings > 0 ? (top5Total / totalListings) * 100 : 0

  return {
    agencies: processedAgencies,
    totalListings,
    totalAgencies: processedAgencies.length,
    marketLeader: processedAgencies[0] || null,
    top5Share,
  }
}

type TranslationFn = (key: string, options?: Record<string, unknown>) => string

const defaultT: TranslationFn = (key: string) => {
  const parts = key.split('.')
  return parts[parts.length - 1] || key
}

const getCategoryLabels = (viewType: ViewType, t: TranslationFn) => ({
  tier1: t(`agencyAnalysis.treemap.ranges.${viewType}.tier1`),
  tier2: t(`agencyAnalysis.treemap.ranges.${viewType}.tier2`),
  tier3: t(`agencyAnalysis.treemap.ranges.${viewType}.tier3`),
  tier4: t(`agencyAnalysis.treemap.ranges.${viewType}.tier4`),
})

export const categorizeAgencies = (
  agencies: AgencyData[],
  viewType: ViewType = 'listings',
  t: TranslationFn = defaultT
) => {
  const labels = getCategoryLabels(viewType, t)

  const groups = {
    [labels.tier1]: [] as AgencyData[],
    [labels.tier2]: [] as AgencyData[],
    [labels.tier3]: [] as AgencyData[],
    [labels.tier4]: [] as AgencyData[],
  }

  const large = labels.tier1
  const medium = labels.tier2
  const small = labels.tier3
  const independent = labels.tier4

  agencies.forEach((agency) => {
    let category: keyof typeof groups

    switch (viewType) {
      case 'value':
        // Categorize by total market value
        if (agency.totalValue >= 1000000000) {
          // 1B+ CHF
          category = large
        } else if (agency.totalValue >= 200000000) {
          // 200M+ CHF
          category = medium
        } else if (agency.totalValue >= 50000000) {
          // 50M+ CHF
          category = small
        } else {
          category = independent
        }
        break

      case 'regions':
        // Categorize by regional coverage
        if (agency.regionCount >= 15) {
          // 15+ regions
          category = large
        } else if (agency.regionCount >= 8) {
          // 8-14 regions
          category = medium
        } else if (agency.regionCount >= 3) {
          // 3-7 regions
          category = small
        } else {
          // 1-2 regions
          category = independent
        }
        break

      default: // 'listings'
        // Categorize by listing count
        if (agency.listingCount >= 1000) {
          category = large
        } else if (agency.listingCount >= 200) {
          category = medium
        } else if (agency.listingCount >= 50) {
          category = small
        } else {
          category = independent
        }
    }

    groups[category].push(agency)
  })

  return groups
}

export const getTreemapData = (
  agencies: AgencyData[],
  viewType: ViewType,
  t: TranslationFn = defaultT
) => {
  const groups = categorizeAgencies(agencies, viewType, t)
  const colors = getChartColors()

  const children = []
  let colorIndex = 0

  for (const [groupName, groupAgencies] of Object.entries(groups)) {
    if (groupAgencies.length === 0) continue

    const groupChildren = groupAgencies.map((agency) => {
      let value: number
      switch (viewType) {
        case 'value':
          value = agency.totalValue
          break
        case 'regions':
          value = agency.regionCount
          break
        default:
          value = agency.listingCount
      }

      return {
        name: agency.name,
        value,
        data: agency,
        itemStyle: {
          color: colors[colorIndex++ % colors.length],
        },
      }
    })

    children.push({
      name: groupName,
      value: groupChildren.reduce((sum, child) => sum + child.value, 0),
      children: groupChildren,
    })
  }

  return {
    name: t('agencyAnalysis.treemap.swissRealEstateMarket'),
    children,
  }
}

export const getChartColors = () => [
  '#00ade6',
  '#33c0ed',
  '#66d3f3',
  '#99e5fa', // Primary blue shades
  '#10b981',
  '#34d399',
  '#6ee7b7',
  '#f59e0b',
  '#fbbf24',
  '#fde68a',
  '#8b5cf6',
  '#a78bfa',
  '#c4b5fd',
  '#ef4444',
  '#f87171',
  '#fca5a5',
  '#06b6d4',
  '#22d3ee',
  '#67e8f9',
  '#ec4899',
  '#f472b6',
  '#f9a8d4',
]

export const getConcentrationLevel = (top5Share: number): string => {
  if (top5Share > 50) return 'High concentration'
  if (top5Share > 30) return 'Moderate concentration'
  return 'Low concentration'
}

export const generateInsights = (
  data: AgencyAnalysisData,
  isRental: boolean = false,
  t: TranslationFn = defaultT
): Insight[] => {
  const insights: Insight[] = []

  if (data.agencies.length === 0) return insights

  // 1. Market concentration insight
  const top5Agencies = [...data.agencies]
    .sort((a, b) => b.listingCount - a.listingCount)
    .slice(0, 5)
  const top5ListingCount = top5Agencies.reduce(
    (sum, a) => sum + a.listingCount,
    0
  )
  insights.push({
    title: t('charts.insights.agency.marketConcentration'),
    description: t('charts.insights.descriptions.agency.marketConcentration', {
      percentage: data.top5Share.toFixed(0),
      count: top5ListingCount.toLocaleString(),
    }),
  })

  // // 2. Agency distribution by size (based on listings for insights)
  // const groups = categorizeAgencies(data.agencies, 'listings')
  // const largeCount = groups['Large Agencies'].length
  // const mediumCount = groups['Medium Agencies'].length
  // const smallCount = groups['Small Agencies'].length
  // const independentCount = groups['Independent Agencies'].length

  // insights.push({
  //   title: 'Agency Distribution',
  //   description: `The market consists of ${largeCount} large agencies (1000+ listings), ${mediumCount} medium agencies (200-999), ${smallCount} small agencies (50-199), and ${independentCount} independent agencies (<50 listings).`,
  // })

  // // 3. Geographic coverage
  // const avgRegions =
  //   data.agencies.reduce((sum, a) => sum + a.regionCount, 0) /
  //   data.agencies.length
  // const multiRegionalAgencies = data.agencies.filter(
  //   (a) => a.regionCount > 5
  // ).length

  // insights.push({
  //   title: 'Geographic Coverage',
  //   description: `Agencies operate in an average of ${avgRegions.toFixed(1)} regions, with ${multiRegionalAgencies} agencies having a presence in more than 5 regions, showing ${multiRegionalAgencies > data.agencies.length * 0.2 ? 'significant' : 'limited'} geographic diversification.`,
  // })

  // 4. Value concentration
  const totalValue = data.agencies.reduce((sum, a) => sum + a.totalValue, 0)
  const top10ValueAgencies = [...data.agencies]
    .sort((a, b) => b.totalValue - a.totalValue)
    .slice(0, 10)
  const top10Value = top10ValueAgencies.reduce(
    (sum, a) => sum + a.totalValue,
    0
  )
  const valueConcentration = (top10Value / totalValue) * 100

  insights.push({
    title: t('charts.insights.agency.valueDistribution'),
    description: t('charts.insights.descriptions.agency.valueDistribution', {
      percentage: valueConcentration.toFixed(0),
      value: formatPrice(top10Value, isRental),
    }),
  })

  // 5. Average performance
  const avgListingsPerAgency = data.totalListings / data.totalAgencies
  const highPerformers = data.agencies.filter(
    (a) => a.listingCount > avgListingsPerAgency * 2
  ).length

  const distributionType =
    highPerformers > data.totalAgencies * 0.3
      ? t('charts.common.concentrated') || 'concentrated'
      : t('charts.common.distributed') || 'distributed'
  insights.push({
    title: t('charts.insights.agency.performanceDistribution'),
    description: t(
      'charts.insights.descriptions.agency.performanceDistribution',
      {
        avgListings: Math.round(avgListingsPerAgency),
        distribution: distributionType,
        count: data.totalAgencies,
      }
    ),
  })

  return insights
}
